import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { StepperInput } from 'foundations-components/transitional/components/StepperInput';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import styled from 'foundations-theming/utils';
import { PriceDisplay } from 'pricing-pages-ui-components/price/PriceDisplay';
import { useId } from 'react';
import debounce from 'react-utils/debounce';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { planBuilderQuantityUpdated, planBuilderSkuRemovedThunk } from '../../../actions/PlanBuilderActions';
import { useCountryCode, useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { planBuilderIsLoadingSelector } from '../../../selectors/PlanBuilderSelectors';
import { billingNameKeySelector, productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { trackPlanBuilderInteraction } from '../../../v2/utils/tracking/trackingEventUtils';
import SkeletonLoadWrapper from '../../common/loading/SkeletonLoadWrapper';
import { BundleIncludesDisplay } from './BundleIncludesDisplay';
import ShoppingCartQuantityPack from './ShoppingCartQuantityPack';
import { useShoppingCartPrice, useShoppingCartProductConfigInfo, useShoppingCartSkuInfo } from './shoppingCartHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ShoppingCartItemContainer = styled.div.withConfig({
  displayName: "ShoppingCartItem__ShoppingCartItemContainer"
})(({
  theme
}) => {
  return {
    backgroundColor: theme.color['fill-surface-raised'],
    paddingBlock: theme.space[400],
    paddingInline: theme.space[600],
    position: 'relative',
    '&:not(:last-child)': {
      marginBottom: theme.space[200]
    }
  };
});
const ShoppingCartItem = ({
  skuId,
  isCloseable = true
}) => {
  const dispatch = useAppDispatch();
  const isPlanBuilderLoading = useAppSelector(planBuilderIsLoadingSelector);
  const country = useCountryCode();
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const currencyCode = useDisplayedCurrencyCode();
  const isRecurring = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }).chargeType === RECURRING);
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const {
    productConfiguration,
    quantityPackProductConfigurations
  } = useShoppingCartProductConfigInfo({
    skuId,
    isAppAuth,
    country
  });
  const {
    skuIdIsQuantityPack,
    shouldShowQuantityInTitle,
    isQuantifiableHubSkuAtProOrEnt
  } = useShoppingCartSkuInfo({
    productConfiguration
  });
  const {
    apiName
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const price = useShoppingCartPrice({
    productConfiguration
  });
  const itemTitleId = useId();
  const handleRemove = () => {
    trackPlanBuilderInteraction({
      component: 'ShoppingCartItem',
      action: 'removed item',
      skuId,
      productName: apiName,
      activeCurrencyCode: currencyCode
    });
    dispatch(planBuilderSkuRemovedThunk({
      skuId
    }));
  };
  const handleUpdateQuantity = debounce(event => {
    const newValue = parseInt(event.target.value, 10);
    dispatch(planBuilderQuantityUpdated({
      skuId,
      quantity: newValue
    }));
  }, 500);

  // Show loading skeleton for quantity packs, Milestone 5
  // Show loading skeleton for products if ungated to Milestone 8 (pending loading state design)
  const showLoadingSkeleton = isPlanBuilderLoading && skuIdIsQuantityPack;
  return /*#__PURE__*/_jsxs(ShoppingCartItemContainer, {
    "data-selenium-class": "shopping-cart-item-container",
    "data-selenium-id": `${apiName}-shopping-cart-item`,
    children: [/*#__PURE__*/_jsxs(TFlex, {
      direction: "row",
      justify: "between",
      children: [/*#__PURE__*/_jsx(Text, {
        typography: "body-125",
        marginInlineEnd: "600",
        id: itemTitleId,
        children: shouldShowQuantityInTitle ? /*#__PURE__*/_jsx(FormattedMessage, {
          "data-selenium-class": "shopping-cart-item-title",
          message: `pricing-pages-ui.productNamesV2.${billingNameKey}.priceBreakdownTitle`,
          options: {
            count: productConfiguration.quantity
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          "data-selenium-class": "shopping-cart-item-title",
          message: `pricing-pages-ui.productNamesV2.${billingNameKey}.title`
        })
      }), /*#__PURE__*/_jsxs(Flex, {
        alignItems: "end",
        flexDirection: "column",
        children: [isQuantifiableHubSkuAtProOrEnt && /*#__PURE__*/_jsx(FormControl, {
          "aria-labelledby": itemTitleId,
          marginInlineStart: "200",
          marginInlineEnd: "200",
          children: /*#__PURE__*/_jsx(StepperInput, {
            value: productConfiguration.quantity,
            min: 1,
            onChange: handleUpdateQuantity,
            minInlineSize: "84px",
            maxInlineSize: "100px",
            "data-selenium-class": "shopping-cart-item-stepper"
          })
        }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          heights: [24],
          widths: [90],
          isLoading: showLoadingSkeleton || !price,
          children: /*#__PURE__*/_jsxs(View, {
            marginInline: "200",
            as: "span",
            children: [/*#__PURE__*/_jsx(PriceDisplay, {
              price: price,
              currencyCode: currencyCode
            }), isRecurring && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.priceDisplay.perMonth"
            })]
          })
        })]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      children: [quantityPackProductConfigurations.map(({
        skuId: quantityPackSkuId,
        quantity
      }) => /*#__PURE__*/_jsx(ShoppingCartQuantityPack, {
        skuId: quantityPackSkuId,
        quantity: quantity,
        inline: quantityPackProductConfigurations.length === 1
      }, quantityPackSkuId)), /*#__PURE__*/_jsx(BundleIncludesDisplay, {
        skuId: skuId
      })]
    }), isCloseable && !showLoadingSkeleton && /*#__PURE__*/_jsx(CloseButton, {
      position: "absolute",
      insetBlockStart: "200",
      insetInlineEnd: "200",
      color: "icon-core-subtle",
      onClick: handleRemove,
      size: "medium",
      "data-selenium-class": "shopping-cart-item-remove"
    })]
  });
};
export default ShoppingCartItem;